import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { publicConsumerApi, type IntakeImagePayload, type IntakeShotCapture } from "@/lib/api/public-consumer"
import type { ImageEvaluation } from "@/lib/api/agents"
import { createLimiter } from "@/components/estimates/quick-estimate/concurrency"
import { consumerVerdict } from "../verdict"
import { checkPhoto } from "../brain/check-photo"
import type { TurnJudgement } from "../motion/turn"
import type { CaptureState, PhoneCheck, PlannedShot, ShotId, ShotRecord, ShotSource } from "../types"
import type { PreviewRegistry } from "./preview-registry"

// Upload pipeline for the consumer walk. Each photo, the moment it's taken:
//   0. is looked at on the phone by the car-view brain (brain/check-photo.ts),
//      in parallel with everything below and offline too;
//   1. shows instantly from a blob preview (PreviewRegistry);
//   2. is evaluated by the AI in the background (3 at a time, 2 retries with
//      backoff, a request timeout; parked as "queued" while offline and
//      resumed on reconnect);
//   3. is attached to the lead with final:false (debounced, one request at a
//      time) so the desktop can mirror the walk and a reload can resume.
// "Send photos" waits for the stragglers, then sends final:true — the only
// call that runs the damage analysis. Only one final send runs at a time.
//
// Removing a photo deletes EVERY copy that reached the lead under that slot
// (after any attach in flight lands), and keeps trying while offline — a
// photo of the wrong car must not stay on someone's estimate.
//
// Every async result is checked against a per-key generation number, so a
// retake, a removal or a re-key while a request is in flight can never be
// overwritten by the stale answer.

const RETRY_DELAYS_MS = [1000, 4000]
const ATTACH_DEBOUNCE_MS = 800
const FINAL_WAIT_MS = 20_000

interface Job {
  file: File
  planned: PlannedShot | null
  gen: number
}

export interface ShotUploadActions {
  putShot: (id: ShotId, record: ShotRecord) => void
  patchShot: (key: string, patch: Partial<ShotRecord>) => void
  removeShot: (key: string) => void
  addExtras: (records: ShotRecord[]) => void
  markPhotosSynced: (synced: boolean) => void
}

export type FinalResult = "sent" | "offline" | "failed"

export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  )
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener("online", up)
    window.addEventListener("offline", down)
    return () => {
      window.removeEventListener("online", up)
      window.removeEventListener("offline", down)
    }
  }, [])
  return online
}

function allRecords(capture: CaptureState): ShotRecord[] {
  return [...(Object.values(capture.shots) as ShotRecord[]), ...capture.extras]
}

export function payloadOf(record: ShotRecord, planned: PlannedShot | null): IntakeImagePayload {
  return {
    imageUrl: record.uploadedUrl,
    side: record.plannedSide ?? record.detectedSide ?? "FRONT_CENTER",
    qualityScore: record.qualityScore,
    qualified: record.qualified,
    ...(record.description && { description: record.description }),
    metadata: {
      shot: {
        id: record.key,
        kind: planned?.kind ?? "extra",
        zone: planned?.zone ?? null,
        ...(record.capture && { capture: record.capture }),
      },
    },
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function useShotUploads({
  token,
  plan,
  capture,
  actions,
  registry,
}: {
  token: string | null
  plan: readonly PlannedShot[]
  capture: CaptureState
  actions: ShotUploadActions
  registry: PreviewRegistry
}) {
  const online = useOnline()
  const [limiter] = useState(() => createLimiter(3))

  // Latest props for async callbacks (updated after every commit).
  const captureRef = useRef(capture)
  const planRef = useRef(plan)
  const tokenRef = useRef(token)
  const actionsRef = useRef(actions)
  useLayoutEffect(() => {
    captureRef.current = capture
    planRef.current = plan
    tokenRef.current = token
    actionsRef.current = actions
  })

  const jobs = useRef(new Map<string, Job>())
  const gens = useRef(new Map<string, number>())
  // Every URL that reached the lead, per record key.
  const attachedUrls = useRef(new Map<string, Set<string>>())
  // Deletes that still have to reach the server (offline, failed).
  const pendingDeletes = useRef(new Set<string>())
  const attachTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attachInFlight = useRef<Promise<void> | null>(null)
  const attachAgain = useRef(false)
  const finalInFlight = useRef<Promise<FinalResult> | null>(null)

  const recordOf = (key: string): ShotRecord | undefined => {
    const c = captureRef.current
    return c.shots[key as ShotId] ?? c.extras.find((r) => r.key === key)
  }
  const plannedFor = (key: string) => planRef.current.find((s) => s.id === key) ?? null
  const isCurrent = (key: string, gen: number) => jobs.current.get(key)?.gen === gen
  const bumpGen = (key: string) => {
    const gen = (gens.current.get(key) ?? 0) + 1
    gens.current.set(key, gen)
    return gen
  }

  const rememberAttached = (key: string, url: string) => {
    const set = attachedUrls.current.get(key) ?? new Set<string>()
    set.add(url)
    attachedUrls.current.set(key, set)
  }

  // One attach request at a time; anything that became ready meanwhile goes
  // in the next one.
  const attachNow = useCallback((): Promise<void> => {
    if (attachInFlight.current) {
      attachAgain.current = true
      return attachInFlight.current
    }
    const t = tokenRef.current
    const ready = allRecords(captureRef.current).filter(
      (r) => r.status === "evaluated" && r.uploadedUrl,
    )
    if (!t || ready.length === 0) return Promise.resolve()
    const run = (async () => {
      try {
        await publicConsumerApi.appendImages(
          t,
          ready.map((r) => payloadOf(r, plannedFor(r.key))),
          { final: false },
        )
        for (const sent of ready) {
          rememberAttached(sent.key, sent.uploadedUrl)
          // Only if that slot still shows the photo we just attached.
          if (recordOf(sent.key)?.uploadedUrl === sent.uploadedUrl) {
            actionsRef.current.patchShot(sent.key, { status: "attached" })
          }
        }
      } catch {
        // Left as "evaluated" — the next attach or the final send retries.
      }
    })()
    attachInFlight.current = run.finally(() => {
      attachInFlight.current = null
      if (attachAgain.current) {
        attachAgain.current = false
        void attachNow()
      }
    })
    return attachInFlight.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scheduleAttach = useCallback(() => {
    if (attachTimer.current) clearTimeout(attachTimer.current)
    attachTimer.current = setTimeout(() => void attachNow(), ATTACH_DEBOUNCE_MS)
  }, [attachNow])

  useEffect(
    () => () => {
      if (attachTimer.current) clearTimeout(attachTimer.current)
    },
    [],
  )

  const flushDeletes = useCallback(async () => {
    const t = tokenRef.current
    if (!t || !navigator.onLine) return
    for (const url of [...pendingDeletes.current]) {
      try {
        await publicConsumerApi.removeImage(t, url)
        pendingDeletes.current.delete(url)
      } catch {
        // Stays queued; retried on reconnect and before the final send.
      }
    }
  }, [])

  const run = useCallback(
    async (key: string, gen: number) => {
      const job = jobs.current.get(key)
      if (!job || job.gen !== gen) return
      const patch = (p: Partial<ShotRecord>) => {
        if (isCurrent(key, gen)) actionsRef.current.patchShot(key, p)
      }
      if (!navigator.onLine) {
        patch({ status: "queued" })
        return
      }
      patch({ status: "uploading" })
      for (let attempt = 0; ; attempt++) {
        try {
          const { evaluation } = await limiter.run(() =>
            publicConsumerApi.evaluateImage(job.file, job.planned?.side, job.planned?.kind),
          )
          if (!isCurrent(key, gen)) return
          patch({
            status: "evaluated",
            uploadedUrl: evaluation.imageUrl,
            detectedSide: evaluation.side ?? null,
            qualityScore: evaluation.qualityScore,
            qualified: evaluation.qualified,
            qualityIssues: evaluation.qualityIssues ?? [],
            description: evaluation.description ?? null,
            verdict: consumerVerdict(evaluation, job.planned),
          })
          scheduleAttach()
          // Landed after "Send photos": the analysis has to run again.
          if (captureRef.current.photosDoneAt !== null) actionsRef.current.markPhotosSynced(false)
          return
        } catch {
          if (!isCurrent(key, gen)) return
          if (!navigator.onLine) {
            patch({ status: "queued" })
            return
          }
          if (attempt >= RETRY_DELAYS_MS.length) {
            patch({ status: "failed" })
            return
          }
          await sleep(RETRY_DELAYS_MS[attempt])
          if (!isCurrent(key, gen)) return
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limiter, scheduleAttach],
  )

  // Back online → resume everything that was parked, send queued deletes.
  useEffect(() => {
    if (!online) return
    for (const [key, job] of jobs.current) {
      if (recordOf(key)?.status === "queued") void run(key, job.gen)
    }
    void flushDeletes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, run, flushDeletes])

  const startJob = useCallback(
    (key: string, file: File, planned: PlannedShot | null) => {
      const gen = bumpGen(key)
      jobs.current.set(key, { file, planned, gen })
      void run(key, gen)
    },
    [run],
  )

  const submit = useCallback(
    (
      key: string,
      file: File,
      opts: {
        source: ShotSource
        planned?: PlannedShot | null
        // How far the phone turned since the last whole-car photo (live camera).
        turn?: TurnJudgement | null
        // How the live camera took it (timings).
        capture?: IntakeShotCapture | null
      },
    ) => {
      const planned = opts.planned === undefined ? plannedFor(key) : opts.planned
      const prev = recordOf(key)
      // A retake replaces a photo that may already be on the lead: remember it
      // so a later "Remove" deletes that one too.
      if (prev?.uploadedUrl && prev.status === "attached") rememberAttached(key, prev.uploadedUrl)
      const record: ShotRecord = {
        key,
        status: navigator.onLine ? "uploading" : "queued",
        previewUrl: registry.set(key, file),
        uploadedUrl: "",
        plannedSide: planned?.side ?? null,
        detectedSide: null,
        qualityScore: 0,
        qualified: false,
        qualityIssues: [],
        description: null,
        verdict: null,
        phone: undefined,
        accepted: false,
        source: opts.source,
        retakes: prev ? prev.retakes + 1 : 0,
        takenAt: Date.now(),
        capture: opts.capture ?? null,
      }
      if (planned) actionsRef.current.putShot(planned.id, record)
      else if (prev) actionsRef.current.patchShot(key, record)
      else actionsRef.current.addExtras([record])
      startJob(key, file, planned)
      const gen = gens.current.get(key)
      void checkPhoto(file, planned, opts.turn ?? null).then((phone) => {
        if (gen !== undefined && isCurrent(key, gen)) actionsRef.current.patchShot(key, { phone })
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, startJob],
  )

  const retry = useCallback(
    (key: string) => {
      const job = jobs.current.get(key)
      if (job) void run(key, job.gen)
    },
    [run],
  )

  const remove = useCallback(
    (key: string) => {
      const record = recordOf(key)
      bumpGen(key)
      jobs.current.delete(key)
      registry.release(key)
      actionsRef.current.removeShot(key)
      void (async () => {
        // Let an attach that already included this photo land first.
        if (attachInFlight.current) await attachInFlight.current.catch(() => {})
        const urls = new Set(attachedUrls.current.get(key) ?? [])
        if (record?.uploadedUrl && record.status === "attached") urls.add(record.uploadedUrl)
        attachedUrls.current.delete(key)
        for (const url of urls) pendingDeletes.current.add(url)
        await flushDeletes()
      })()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, flushDeletes],
  )

  // A planned slot that the new walk no longer has (the damage answer
  // changed): keep the photo as an extra, carrying its upload along.
  const orphan = useCallback(
    (id: string) => {
      const record = recordOf(id)
      if (!record) return
      const newKey = `extra:${id}:${record.takenAt}`
      // A check still running belongs to the old step: drop it.
      const phone = record.phone ?? null
      const job = jobs.current.get(id)
      bumpGen(id)
      jobs.current.delete(id)
      registry.rename(id, newKey)
      const attached = attachedUrls.current.get(id)
      if (attached) {
        attachedUrls.current.set(newKey, attached)
        attachedUrls.current.delete(id)
      }
      actionsRef.current.removeShot(id)
      actionsRef.current.addExtras([{ ...record, key: newKey, phone }])
      // Still on its way up: finish the upload under the new key.
      if (!record.uploadedUrl && job) startJob(newKey, job.file, null)
      else if (record.status === "evaluated") scheduleAttach()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, startJob, scheduleAttach],
  )

  // A gallery photo that was already uploaded + checked (to learn its side)
  // and has now been placed: file it under its slot (or as an extra).
  const adopt = useCallback(
    (
      key: string,
      file: File,
      evaluation: ImageEvaluation,
      planned: PlannedShot | null,
      source: ShotSource,
      phone: PhoneCheck | null = null,
    ) => {
      bumpGen(key)
      jobs.current.delete(key)
      const prev = recordOf(key)
      const record: ShotRecord = {
        key,
        status: "evaluated",
        previewUrl: registry.set(key, file),
        uploadedUrl: evaluation.imageUrl,
        plannedSide: planned?.side ?? evaluation.side ?? null,
        detectedSide: evaluation.side ?? null,
        qualityScore: evaluation.qualityScore,
        qualified: evaluation.qualified,
        qualityIssues: evaluation.qualityIssues ?? [],
        description: evaluation.description ?? null,
        verdict: consumerVerdict(evaluation, planned),
        phone,
        accepted: false,
        source,
        retakes: prev ? prev.retakes + 1 : 0,
        takenAt: Date.now(),
      }
      if (planned) actionsRef.current.putShot(planned.id, record)
      else actionsRef.current.addExtras([record])
      scheduleAttach()
      if (captureRef.current.photosDoneAt !== null) actionsRef.current.markPhotosSynced(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, scheduleAttach],
  )

  // "Move to…": re-file a photo under another slot (or back to extras).
  const move = useCallback(
    (fromKey: string, to: PlannedShot | null) => {
      const record = recordOf(fromKey)
      if (!record) return
      const toKey = to ? to.id : `extra:${fromKey}:${Date.now()}`
      if (toKey === fromKey) return
      const job = jobs.current.get(fromKey)
      bumpGen(fromKey)
      jobs.current.delete(fromKey)
      registry.rename(fromKey, toKey)
      const attached = attachedUrls.current.get(fromKey)
      if (attached) {
        attachedUrls.current.set(toKey, attached)
        attachedUrls.current.delete(fromKey)
      }
      actionsRef.current.removeShot(fromKey)
      const moved: ShotRecord = {
        ...record,
        key: toKey,
        plannedSide: to?.side ?? record.detectedSide,
        // Re-sent under the new shot id (the server retires the old copy).
        status: record.uploadedUrl ? "evaluated" : record.status,
        // The person chose the slot themselves: no second opinion.
        phone: null,
        accepted: true,
      }
      if (to) actionsRef.current.putShot(to.id, moved)
      else actionsRef.current.addExtras([moved])
      if (!record.uploadedUrl && job) startJob(toKey, job.file, to)
      else if (record.uploadedUrl) scheduleAttach()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, startJob, scheduleAttach],
  )

  // "Send photos": wait for in-flight checks, then attach anything missing and
  // run the analysis once. Single-flight; safe to call again (server dedupes).
  const sendFinal = useCallback((): Promise<FinalResult> => {
    if (finalInFlight.current) return finalInFlight.current
    const go = async (): Promise<FinalResult> => {
      const t = tokenRef.current
      if (!t) return "failed"
      const started = Date.now()
      while (
        allRecords(captureRef.current).some((r) => r.status === "uploading") &&
        Date.now() - started < FINAL_WAIT_MS
      ) {
        await sleep(250)
      }
      if (!navigator.onLine) return "offline"
      if (attachTimer.current) clearTimeout(attachTimer.current)
      if (attachInFlight.current) await attachInFlight.current.catch(() => {})
      await flushDeletes()
      const ready = allRecords(captureRef.current).filter((r) => r.uploadedUrl)
      const images = ready.map((r) => payloadOf(r, plannedFor(r.key)))
      for (let attempt = 0; ; attempt++) {
        try {
          await publicConsumerApi.appendImages(t, images, { final: true })
          for (const sent of ready) {
            rememberAttached(sent.key, sent.uploadedUrl)
            if (recordOf(sent.key)?.uploadedUrl === sent.uploadedUrl && sent.status !== "attached") {
              actionsRef.current.patchShot(sent.key, { status: "attached" })
            }
          }
          actionsRef.current.markPhotosSynced(true)
          return "sent"
        } catch {
          if (!navigator.onLine) return "offline"
          if (attempt >= RETRY_DELAYS_MS.length) return "failed"
          await sleep(RETRY_DELAYS_MS[attempt])
        }
      }
    }
    finalInFlight.current = go().finally(() => {
      finalInFlight.current = null
    })
    return finalInFlight.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushDeletes])

  const pending = useMemo(
    () =>
      allRecords(capture).filter((r) => r.status === "uploading" || r.status === "queued").length,
    [capture],
  )

  return useMemo(
    () => ({ online, pending, submit, retry, remove, orphan, adopt, move, sendFinal }),
    [online, pending, submit, retry, remove, orphan, adopt, move, sendFinal],
  )
}

export type ShotUploads = ReturnType<typeof useShotUploads>
